import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Building2,
  CheckCircle2,
  ChevronLeft,
  Handshake,
  Layers3,
  Network,
  Shield,
  Sparkles,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SEO_IMAGES } from '../utils/seo';

export default function EcosystemPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Ecossistema Saúde | BuscaDoutor';
  }, []);

  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    cnpj: '',
    assunto: '',
  });

  const handlePartnerFormChange = (field, value) => {
    setPartnerForm((current) => ({ ...current, [field]: value }));
  };

  const handlePartnerSubmit = (event) => {
    event.preventDefault();

    const subject = `Parceria BuscaDoutor - ${partnerForm.empresa || partnerForm.nome}`;
    const body = [
      'Olá, tenho interesse em ser parceiro do BuscaDoutor.',
      '',
      `Nome: ${partnerForm.nome}`,
      `E-mail: ${partnerForm.email}`,
      `Telefone: ${partnerForm.telefone}`,
      `Empresa: ${partnerForm.empresa}`,
      `CNPJ: ${partnerForm.cnpj}`,
      '',
      `Assunto: ${partnerForm.assunto}`,
    ].join('\n');

    window.location.href = `mailto:support@buscadoutor.com.br?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setPartnerModalOpen(false);
  };

  const pillars = [
    {
      icon: Layers3,
      title: 'Parceiros e Soluções',
      eyebrow: 'Integrações, produtos e inteligência',
      body: 'Um espaço para operadoras, laboratórios, clínicas, sistemas de gestão e soluções BuscaDoutor que reduzem atrito na jornada do paciente e geram mais clareza para o profissional.',
      points: ['Convênios e redes credenciadas', 'Ferramentas para clínicas', 'Analytics e oportunidades por cidade'],
      actionLabel: 'Quero ser parceiro',
      action: () => setPartnerModalOpen(true),
      accent: 'bg-[#EAF6F1] text-primary',
    },
    {
      icon: BookOpen,
      title: 'Blog de Saúde',
      eyebrow: 'Conteúdo editorial e técnico',
      body: 'Artigos para médicos, dentistas, clínicas e comunidade de saúde, organizados entre informações gerais e especialidades médicas, com foco em qualidade, atualização e SEO.',
      points: ['Informações gerais', 'Especialidades médicas', 'Artigos indexáveis no Google'],
      actionLabel: 'Acessar blog',
      href: '/blog',
      accent: 'bg-[#FFF4D6] text-amber-800',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7FBF9]" data-testid="ecosystem-page">
      <SEOHead
        title="Ecossistema Saúde | BuscaDoutor"
        description="Parceiros, soluções BuscaDoutor e conteúdo de saúde conectados para médicos, dentistas, clínicas, pacientes e empresas do setor."
        canonicalUrl="/ecosistema"
        imageUrl={SEO_IMAGES.ecosystem}
      />
      <Navbar />

      <section className="relative overflow-hidden border-b border-[#DDE8E4] bg-[#F5FAF8]">
        <div className="container-app py-10 md:py-16">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-sm text-[#59706A] transition-colors hover:text-primary"
            data-testid="ecosystem-back-link"
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </Link>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:items-center">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/10 bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm">
                <Network className="h-4 w-4" />
                Ecossistema BuscaDoutor
              </div>

              <h1 className="text-3xl font-light leading-tight tracking-tight text-[#1A2421] sm:text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Saúde digital mais conectada, útil e confiável.
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#59706A] md:text-lg">
                O Ecossistema Saúde reúne parceiros, soluções e conteúdo para aproximar pacientes, profissionais, clínicas e empresas do setor em uma jornada mais simples e transparente.
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setPartnerModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(28,78,66,0.18)] transition-colors hover:bg-[#2D6D5D]"
                  data-testid="ecosystem-hero-partner-button"
                >
                  <Handshake className="h-4 w-4" />
                  Quero ser parceiro
                </button>
                <Link to="/blog" data-testid="ecosystem-hero-blog-link">
                  <Button size="lg" variant="outline" className="w-full rounded-full border-primary/30 bg-white px-7 text-primary hover:bg-primary/5 sm:w-auto">
                    Ler o blog
                  </Button>
                </Link>
              </div>
            </div>

            <div className="rounded-[28px] border border-[#DDE8E4] bg-white p-3 shadow-[0_18px_55px_rgba(28,78,66,0.10)]">
              <img
                src={SEO_IMAGES.ecosystem}
                alt="Ecossistema Saúde BuscaDoutor conectando conhecimento, dados e negócios em saúde"
                width="1200"
                height="630"
                className="aspect-[1200/630] w-full rounded-2xl object-contain"
                fetchPriority="high"
              />
            </div>

            <div className="hidden rounded-[34px] border border-white bg-white p-3 shadow-[0_28px_80px_rgba(28,78,66,0.18)]">
              <div className="relative overflow-hidden rounded-[28px] bg-[#102F29] p-5 text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_24%_22%,rgba(240,200,106,0.32),transparent_18%),radial-gradient(circle_at_78%_26%,rgba(101,209,178,0.22),transparent_22%),linear-gradient(145deg,rgba(255,255,255,0.08),transparent_42%)]" />
                <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:34px_34px]" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F0C86A]">Hub de conhecimento e negócios</p>
                      <h2 className="mt-2 text-xl font-semibold">Uma cena viva para parceiros</h2>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-lg backdrop-blur">
                      <Sparkles className="h-5 w-5 text-[#F0C86A]" />
                    </div>
                  </div>

                  <div className="relative mt-8 min-h-[360px] overflow-hidden rounded-[22px] border border-white/10 bg-white/[0.03] [perspective:1200px]">
                    <div className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:36px_36px] [transform:rotateX(63deg)_translateY(88px)_scale(1.22)]" />
                    <div className="absolute inset-x-10 bottom-7 h-24 rounded-full bg-black/35 blur-3xl" />
                    <div className="absolute left-1/2 top-[56%] h-52 w-72 -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-[#F0C86A]/25 [transform:translate(-50%,-50%)_rotateX(66deg)_rotateZ(-18deg)]" />
                    <div className="absolute left-1/2 top-[56%] h-64 w-[22rem] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-white/12 [transform:translate(-50%,-50%)_rotateX(68deg)_rotateZ(24deg)]" />

                    <div className="absolute left-1/2 top-[47%] z-20 h-48 w-48 -translate-x-1/2 -translate-y-1/2">
                      <div className="absolute inset-0 rounded-[42px] bg-[radial-gradient(circle_at_30%_25%,#fff7d6,rgba(240,200,106,.92)_28%,rgba(44,111,94,.88)_62%,rgba(13,45,39,.94)_100%)] shadow-[0_34px_80px_rgba(0,0,0,.38),inset_-28px_-28px_54px_rgba(0,0,0,.28),inset_18px_18px_34px_rgba(255,255,255,.25)] [transform:rotateX(54deg)_rotateZ(-35deg)]" />
                      <div className="absolute -inset-5 rounded-[52px] border border-white/15 [transform:rotateX(54deg)_rotateZ(-35deg)]" />
                      <div className="absolute inset-0 flex items-center justify-center [transform:translateY(-10px)]">
                        <div className="flex h-20 w-20 items-center justify-center rounded-3xl border border-white/25 bg-white/18 shadow-[0_20px_48px_rgba(0,0,0,.25)] backdrop-blur-xl">
                          <Network className="h-9 w-9 text-[#F0C86A]" />
                        </div>
                      </div>
                    </div>

                    {[
                      { icon: BookOpen, label: 'Conhecimento', className: 'left-6 top-8', dot: 'bg-[#F0C86A]' },
                      { icon: Handshake, label: 'Parcerias', className: 'right-5 top-16', dot: 'bg-emerald-200' },
                      { icon: BarChart3, label: 'Dados', className: 'bottom-14 left-10', dot: 'bg-sky-200' },
                      { icon: Building2, label: 'Operação', className: 'bottom-8 right-8', dot: 'bg-rose-200' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div key={item.label} className={`absolute ${item.className} z-30 flex items-center gap-2 rounded-2xl border border-white/14 bg-white/12 px-3 py-2 shadow-[0_18px_42px_rgba(0,0,0,.20)] backdrop-blur-xl`}>
                          <span className={`h-2 w-2 rounded-full ${item.dot} shadow-[0_0_16px_currentColor]`} />
                          <Icon className="h-4 w-4 text-white/80" />
                          <span className="text-xs font-semibold">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur">
                    <p className="text-sm font-semibold">Uma vitrine para parceiros estratégicos</p>
                    <p className="mt-1 text-xs leading-relaxed text-white/62">
                      Conteúdo, dados e soluções conectados em uma experiência única para saúde digital.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden rounded-[32px] border border-white bg-white p-3 shadow-[0_24px_70px_rgba(28,78,66,0.16)]">
              <div className="relative overflow-hidden rounded-[26px] bg-[#102F29] p-5 text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_24%_22%,rgba(240,200,106,0.32),transparent_18%),radial-gradient(circle_at_78%_26%,rgba(101,209,178,0.22),transparent_22%),linear-gradient(145deg,rgba(255,255,255,0.08),transparent_42%)]" />
                <div className="absolute left-8 top-14 h-44 w-44 rounded-full bg-[#F0C86A]/10 blur-3xl" />
                <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-emerald-300/10 blur-3xl" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F0C86A]">Hub de conhecimento e negócios</p>
                      <h2 className="mt-2 text-xl font-semibold">Entre no núcleo do ecossistema</h2>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-lg backdrop-blur">
                      <Sparkles className="h-5 w-5 text-[#F0C86A]" />
                    </div>
                  </div>

                  <div className="relative mt-8 min-h-[330px] [perspective:1100px]">
                    <div className="absolute inset-x-8 bottom-5 h-20 rounded-[999px] bg-black/25 blur-2xl" />

                    <div className="absolute left-1/2 top-[52%] h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#F0C86A]/20 [transform:translate(-50%,-50%)_rotateX(68deg)_rotateZ(-18deg)]" />
                    <div className="absolute left-1/2 top-[52%] h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 [transform:translate(-50%,-50%)_rotateX(68deg)_rotateZ(24deg)]" />
                    <div className="absolute left-1/2 top-[52%] h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-200/20 [transform:translate(-50%,-50%)_rotateX(72deg)_rotateZ(62deg)]" />

                    <div className="absolute left-1/2 top-[48%] z-20 h-28 w-28 -translate-x-1/2 -translate-y-1/2 [transform-style:preserve-3d]">
                      <div className="absolute inset-0 rounded-[28px] border border-white/25 bg-white/15 shadow-[0_22px_48px_rgba(0,0,0,0.30)] backdrop-blur-xl [transform:rotateX(58deg)_rotateZ(-38deg)]" />
                      <div className="absolute inset-3 rounded-[22px] bg-[#F0C86A]/90 shadow-[0_18px_42px_rgba(240,200,106,0.30)] [transform:translateZ(24px)_rotateX(58deg)_rotateZ(-38deg)]" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center [transform:translateZ(58px)]">
                        <Network className="h-8 w-8 text-[#102F29]" />
                        <span className="mt-2 text-[11px] font-bold leading-tight text-[#102F29]">BuscaDoutor</span>
                      </div>
                    </div>

                    {[
                      { icon: BookOpen, title: 'Conhecimento', text: 'conteúdo técnico', className: 'left-0 top-8', transform: '[transform:rotateY(-12deg)_rotateX(8deg)]' },
                      { icon: Handshake, title: 'Negócios', text: 'parcerias úteis', className: 'right-0 top-14', transform: '[transform:rotateY(14deg)_rotateX(8deg)]' },
                      { icon: BarChart3, title: 'Dados', text: 'demanda real', className: 'bottom-12 left-4', transform: '[transform:rotateY(-10deg)_rotateX(-6deg)]' },
                      { icon: Building2, title: 'Operação', text: 'clínicas e redes', className: 'bottom-4 right-5', transform: '[transform:rotateY(12deg)_rotateX(-6deg)]' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div key={item.title} className={`absolute ${item.className} ${item.transform} w-36 rounded-2xl border border-white/14 bg-white/12 p-3 shadow-[0_18px_42px_rgba(0,0,0,0.20)] backdrop-blur-xl transition-transform duration-300 hover:scale-[1.04]`}>
                          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-white/14 text-[#F0C86A]">
                            <Icon className="h-4 w-4" />
                          </div>
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="mt-0.5 text-xs text-white/60">{item.text}</p>
                        </div>
                      );
                    })}

                    <div className="absolute left-[25%] top-[42%] h-2 w-2 rounded-full bg-[#F0C86A] shadow-[0_0_22px_rgba(240,200,106,0.9)]" />
                    <div className="absolute right-[23%] top-[46%] h-2 w-2 rounded-full bg-emerald-200 shadow-[0_0_22px_rgba(167,243,208,0.8)]" />
                    <div className="absolute bottom-[28%] left-[43%] h-1.5 w-1.5 rounded-full bg-white/80 shadow-[0_0_18px_rgba(255,255,255,0.65)]" />
                  </div>

                  <div className="mt-1 grid grid-cols-3 gap-3">
                    {[
                      ['parceiros', 'integrações'],
                      ['conteúdo', 'autoridade'],
                      ['dados', 'decisão'],
                    ].map(([title, label]) => (
                      <div key={title} className="rounded-2xl border border-white/10 bg-white/8 p-3 text-center backdrop-blur">
                        <p className="text-sm font-semibold">{title}</p>
                        <p className="mt-0.5 text-[11px] text-white/55">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16" data-testid="ecosystem-pillars">
        <div className="container-app">
          <div className="mb-8 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Duas frentes principais</span>
            <h2 className="mt-3 text-2xl font-semibold leading-tight text-[#1A2421] md:text-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Um hub para conectar estratégia, conteúdo e oportunidades.
            </h2>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;
              const content = (
                <>
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${pillar.accent}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#59706A] shadow-sm">
                      {pillar.eyebrow}
                    </span>
                  </div>

                  <h3 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit, sans-serif' }}>{pillar.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#59706A] md:text-base">{pillar.body}</p>

                  <div className="mt-5 grid gap-2">
                    {pillar.points.map((point) => (
                      <div key={point} className="flex items-center gap-2 text-sm font-medium text-[#315A50]">
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-primary" />
                        {point}
                      </div>
                    ))}
                  </div>

                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    {pillar.actionLabel}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </>
              );

              if (pillar.href) {
                return (
                  <Link
                    key={pillar.title}
                    to={pillar.href}
                    className="group rounded-[28px] border border-[#DDE8E4] bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_45px_rgba(28,78,66,0.08)] md:p-8"
                    data-testid={`pillar-${pillar.title.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {content}
                  </Link>
                );
              }

              return (
                <button
                  key={pillar.title}
                  type="button"
                  onClick={pillar.action}
                  className="group rounded-[28px] border border-[#DDE8E4] bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_45px_rgba(28,78,66,0.08)] md:p-8"
                  data-testid={`pillar-${pillar.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {content}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="container-app">
          <div className="grid gap-6 rounded-[28px] border border-[#DDE8E4] bg-[#F7FBF9] p-6 md:p-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Por que isso importa</span>
              <h2 className="mt-3 text-2xl font-semibold leading-tight text-[#1A2421] md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                A saúde já é fragmentada demais. A plataforma precisa reduzir ruído.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#59706A] md:text-base">
                Quando dados, conteúdo e parceiros se conectam, o paciente entende melhor suas opções e o profissional ganha uma presença digital mais organizada.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { icon: Shield, text: 'Informações mais claras e verificáveis' },
                { icon: Building2, text: 'Clínicas e profissionais com melhor apresentação' },
                { icon: BookOpen, text: 'Conteúdo técnico separado de ruído e clickbait' },
                { icon: Handshake, text: 'Parcerias com foco em utilidade real' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.text} className="flex gap-3 rounded-2xl bg-white p-4 shadow-sm">
                    <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <p className="text-sm font-medium leading-relaxed text-[#315A50]">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#1C4E42] py-14 text-white md:py-18" data-testid="ecosystem-cta">
        <div className="container-app relative z-10 max-w-3xl">
          <div className="mb-5 inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#F0C86A]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F0C86A]">Quer fazer parte?</span>
          </div>
          <h2 className="text-3xl font-light leading-tight md:text-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Vamos construir uma experiência de saúde mais conectada.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
            Se sua empresa pode melhorar acesso, dados, conteúdo, atendimento ou operação em saúde, queremos conversar.
          </p>
          <button
            type="button"
            onClick={() => setPartnerModalOpen(true)}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#F0C86A] px-7 py-3 font-semibold text-[#1A2421] transition-colors hover:bg-[#F6D985]"
            aria-label="Abrir formulário de parceria"
            data-testid="ecosystem-partnership-button"
          >
            <Handshake className="h-5 w-5" />
            Quero ser parceiro
          </button>
        </div>
      </section>

      <Dialog open={partnerModalOpen} onOpenChange={setPartnerModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="ecosystem-partnership-modal">
          <DialogHeader>
            <DialogTitle>Quero ser parceiro</DialogTitle>
            <DialogDescription>
              Preencha seus dados para que nossa equipe entre em contato.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handlePartnerSubmit} className="space-y-4" data-testid="ecosystem-partnership-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="partner-name">Nome</Label>
                <Input
                  id="partner-name"
                  value={partnerForm.nome}
                  onChange={(event) => handlePartnerFormChange('nome', event.target.value)}
                  placeholder="Seu nome"
                  required
                  data-testid="partner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner-email">E-mail</Label>
                <Input
                  id="partner-email"
                  type="email"
                  value={partnerForm.email}
                  onChange={(event) => handlePartnerFormChange('email', event.target.value)}
                  placeholder="voce@empresa.com.br"
                  required
                  data-testid="partner-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner-phone">Telefone</Label>
                <Input
                  id="partner-phone"
                  type="tel"
                  value={partnerForm.telefone}
                  onChange={(event) => handlePartnerFormChange('telefone', event.target.value)}
                  placeholder="(00) 00000-0000"
                  required
                  data-testid="partner-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner-company">Empresa</Label>
                <Input
                  id="partner-company"
                  value={partnerForm.empresa}
                  onChange={(event) => handlePartnerFormChange('empresa', event.target.value)}
                  placeholder="Nome da empresa"
                  required
                  data-testid="partner-company-input"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="partner-cnpj">CNPJ</Label>
                <Input
                  id="partner-cnpj"
                  value={partnerForm.cnpj}
                  onChange={(event) => handlePartnerFormChange('cnpj', event.target.value)}
                  placeholder="00.000.000/0000-00"
                  required
                  data-testid="partner-cnpj-input"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="partner-subject">Assunto</Label>
                <Textarea
                  id="partner-subject"
                  value={partnerForm.assunto}
                  onChange={(event) => handlePartnerFormChange('assunto', event.target.value)}
                  placeholder="Conte brevemente o motivo do contato"
                  rows={3}
                  required
                  data-testid="partner-subject-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="submit" className="w-full bg-primary hover:bg-[#2D6D5D] sm:w-auto">
                Enviar interesse
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
